import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import authService from '../services/AuthService';
import userService from '../services/UserService';

const STORAGE_KEY = 'user';
const SESSION_DURATION_MS = 6 * 24 * 60 * 60 * 1000; // 6 days in milliseconds

function readStoredUser(): { user: any; isAuthenticated: boolean } {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { user: null, isAuthenticated: false };
    const data = JSON.parse(raw);
    const timestamp = data.timestamp;
    if (timestamp != null && timestamp + SESSION_DURATION_MS < Date.now()) {
      localStorage.removeItem(STORAGE_KEY);
      return { user: null, isAuthenticated: false };
    }
    return { user: data, isAuthenticated: true };
  } catch {
    return { user: null, isAuthenticated: false };
  }
}

/**
 * The login response is persisted whole, so the account itself sits one level
 * down under `user`. Older stored sessions may carry the raw column name.
 */
function extractUserRoleId(stored: any): number | null {
  const raw =
    stored?.user?.userRoleId ??
    stored?.user?.UserRoleID ??
    stored?.userRoleId ??
    stored?.UserRoleID;
  if (raw == null) return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

interface AuthContextValue {
  isAuthenticated: boolean;
  user: any;
  userRoleId: number | null;
  login: (payload: { email: string; password: string }) => Promise<any>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState(() => {
    const stored = readStoredUser();
    if (stored.isAuthenticated && stored.user) {
      userService.init({ user: stored.user });
    }
    return stored;
  });

  const login = useCallback(async (payload: { email: string; password: string }) => {
    const user = await authService.login(payload);
    if (user?.token?.length > 0) {
      setState({ user, isAuthenticated: true });
      return user;
    }
    return user;
  }, []);

  const logout = useCallback(() => {
    authService.logout();
    setState({ user: null, isAuthenticated: false });
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      isAuthenticated: state.isAuthenticated,
      user: state.user,
      userRoleId: extractUserRoleId(state.user),
      login,
      logout,
    }),
    [state.isAuthenticated, state.user, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
